// Server tick configuration
export const SERVER_TICK_RATE = 20; // Ticks per second
export const SERVER_TICK_MS = 1000 / SERVER_TICK_RATE;
export const CLIENT_INTERPOLATION_BUFFER_MS = 100; // Interpolation window

// Arena dimensions
export const ARENA_SIZE_X = 200; // Arena width
export const ARENA_SIZE_Y = 80; // Arena height
export const ARENA_SIZE_Z = 200; // Arena depth
export const ARENA_FLOOR_Y = 0; // Floor level

// Player limits
export const MAX_DRONES_PER_PLAYER = 6; // Max swarm size
export const MAX_CARDS_PER_PLAYER = 6; // Max card inventory

// Starter loadout (initial drone seeding when a player joins).
// 3 fixed slots, all Common-tier per balance constraint. Default mirrors the
// historical Striker/Striker/Healer mix and is used when the client omits
// `loadout` from the join options or sends an invalid value.
export const LOADOUT_SLOTS = 3;
// DroneType enum values: Striker=0, Healer=2.
export const DEFAULT_LOADOUT: ReadonlyArray<number> = [0, 0, 2];

// Pickup configuration
export const PICKUP_SPAWN_INTERVAL_MS = 5000; // Spawn interval
export const MAX_PICKUPS_ON_MAP = 30; // Max pickups
export const PICKUP_COLLECT_RADIUS = 8; // Collection radius

// Respawn and invulnerability
export const RESPAWN_DELAY_MS = 3000; // Respawn timer
export const SPAWN_INVULN_MS = 2000; // Invulnerability on spawn

// Drone base stats
export const DRONE_BASE_SPEED = 15; // Base movement speed
export const DRONE_BASE_HEALTH = 100; // Base health
export const DRONE_COLLISION_RADIUS = 0.8; // Collision size

// Knockback on heavy hits (Unit 2). When a single combat damage event delivers
// at least KNOCKBACK_DAMAGE_THRESHOLD damage, the victim is shoved away from
// the attacker at KNOCKBACK_IMPULSE_SPEED units/sec for one tick. Flocking
// recovers the swarm shape on subsequent ticks; we deliberately do NOT touch
// SwarmController/SteeringBehaviors/FormationSolver to preserve the no-drift
// invariants documented in CLAUDE.md.
export const KNOCKBACK_DAMAGE_THRESHOLD = 25; // Damage required to knock back the victim
export const KNOCKBACK_IMPULSE_SPEED = 8.0; // Units/sec applied for one tick

// Support
export const SUPPORT_AURA_RANGE = 12; // Support buff range

// Guardian
export const GUARDIAN_TAUNT_RADIUS = 60; // Enemies inside this radius fire at the taunting Guardian

// Interceptor
export const INTERCEPTOR_SPEED_BONUS = 1.5; // Interceptor speed multiplier

// Formation and flocking
export const FORMATION_SPACING = 8; // Space between drones in formation
export const FLOCK_SEPARATION_RADIUS = 9.5; // Flocking separation
export const FLOCK_ALIGNMENT_RADIUS = 8; // Flocking alignment
export const FLOCK_COHESION_RADIUS = 12; // Flocking cohesion
export const SWARM_MOVE_LOOKAHEAD = FORMATION_SPACING;
// Flocking blend weights for SwarmController.computeSwarmCommands. Applied
// only in ManualMove and TaskPursuit. IdleSettle skips the blend so the
// no-drift invariant (CLAUDE.md #1) holds even though formation spacing (8)
// is inside FLOCK_SEPARATION_RADIUS (9.5); ActiveCard skips it so card-driven
// formations and orbit anchors aren't perturbed by neighbor pull. Separation
// is multiplied by drone maxSpeed at the call site to keep its inverse-square
// output speed-comparable with seek/arrive.
export const FLOCK_SEPARATION_WEIGHT = 0.30;
export const FLOCK_ALIGNMENT_WEIGHT = 0.22;
export const FLOCK_COHESION_WEIGHT = 0.18;

// Arcade swarm movement
export const SWARM_TRAVEL_SPEED = 30;         // Uniform speed for all drones during manual movement
export const SLAB_ROW_SPACING = 4;            // Forward-to-back spacing between slab rows
export const SLAB_LANE_SPACING = 3;           // Side-to-side spacing within a slab row
export const SLAB_FRONT_ROW_MAX = 3;          // Max drones in the front row of the slab
export const LATERAL_CORRECTION_GAIN = 2.0;   // Proportional gain for lateral lane-keeping
export const MAX_LATERAL_CORRECTION = 8.0;    // Cap on lateral correction magnitude
export const CATCH_UP_THRESHOLD = 2.0;        // Forward offset before catch-up kicks in
export const CATCH_UP_GAIN = 1.5;             // Proportional gain for catch-up speed
export const MAX_CATCH_UP = 10.0;             // Cap on catch-up correction magnitude

// Animated orbit formations
export const ORBIT_ANIM_RADIUS = 8;            // Orbit circle radius
export const ORBIT_ANGULAR_SPEED = 1.0;         // Radians per second
export const DIAGONAL_ORBIT_TILT_DEG = 30;      // Tilt angle in degrees

// Emergency unstacking (collision resolution)
export const EMERGENCY_UNSTACK_DIST = 0.4;    // Distance below which emergency unstacking triggers
export const EMERGENCY_UNSTACK_PUSH = 0.3;    // Max positional correction per tick for unstacking

// Colyseus networking
export const COLYSEUS_ROOM_NAME = "battle";
export const MAX_PLAYERS_PER_ROOM = 8; // Room capacity
export const COLYSEUS_PORT = 42567; // Server port

// Match duration
export const MATCH_DURATION_SEC = 300; // 5 minutes

// Dual-mode room registration
export const FREE_ROOM_NAME = COLYSEUS_ROOM_NAME; // alias: "battle"
export const BR_ROOM_NAME = "br";

// Battle Royale configuration
export const BR_SHRINK_SCHEDULE: ReadonlyArray<{ atSec: number; radius: number }> = [
  { atSec: 300, radius: 60 },
  { atSec: 600, radius: 25 },
  { atSec: 900, radius: 10 },
];
export const BR_SCATTER_RADIUS = 80;
export const BR_ZONE_DPS_BY_STAGE: ReadonlyArray<number> = [5, 10, 20];
export const BR_SHRINK_TRANSITION_SEC = 30;
export const BR_LOBBY_MIN_PLAYERS = 2;
export const BR_LOBBY_COUNTDOWN_SEC = 60;
export const BR_MATCH_POST_END_SEC = 30;

// === Unit 6: rematch === BEGIN
// Window the post-match overlay accepts rematch votes. ≥2 votes within
// REMATCH_VOTE_WINDOW_SEC triggers a fresh room of the same gameMode and
// broadcasts `RematchReady` with the new roomId. Otherwise `RematchFailed`.
export const REMATCH_VOTE_WINDOW_SEC = 15;
export const REMATCH_MIN_VOTES = 2;
// === Unit 6: rematch === END

// Training dummy (admin mode target). One dummy player owns four drones —
// one of each role — arranged in an arc so admins can test AoE, multi-target
// focus fire, healer-vs-tank dynamics, and crit averages all in one session.
export const TRAINING_DUMMY_ID = 'dummy-trainer';
export const TRAINING_DUMMY_USERNAME = 'Training Dummy';
// Stable per-drone ids so respawns / serialization stay deterministic.
export const TRAINING_DUMMY_DRONE_IDS = [
  'dummy-striker',
  'dummy-healer',
  'dummy-sniper',
  'dummy-guardian',
] as const;
// Per-drone HP designed for visible burn-down without one-shot kills. The
// CombatResolver / ChoreoUltimate clamps cap incoming damage so HP stays >= 1
// — these values define the "max" each dummy snaps back to after the clamp.
export const TRAINING_DUMMY_HEALTH = 100;
// Each dummy fires its DRONE_ACTIVE_ABILITIES ability on this rolling rotation
// so admins observe ability VFX/effects without depending on client commands.
export const TRAINING_DUMMY_ABILITY_ROTATION_MS = 8000;
// Dummies slowly regen HP while alive and below maxHealth so admin-mode
// damage tests don't permanently park them at HP 1. Applied per-tick AFTER
// damage clamping so a clamped hit (HP 1) doesn't snap back within the same
// tick — only subsequent ticks climb HP via this rate.
export const TRAINING_DUMMY_HP_REGEN_PER_SEC = 5;

// Weighted random spawn tables
export const DRONE_TYPE_WEIGHTS = [0.3, 0.2, 0.2, 0.15, 0.15]; // Spawn weights: Str/Snp/Heal/Int/Sup
export const TIER_WEIGHTS = [0.6, 0.3, 0.1]; // Spawn weights: Common/Rare/Legend
export const PICKUP_TYPE_WEIGHTS = [0.6, 0.4]; // Spawn weights: Drone/Algorithm

// === U5: card mechanics polish === BEGIN
// ChoreoUltimate requires the participating drones to be tightly grouped before
// the AoE damage can land. The check measures each contributing drone's
// distance to the swarm centroid; if any drone is further than this radius the
// AoE skips damage entirely and emits CardEffectFailed. 8u matches the default
// FORMATION_SPACING so a normal in-formation swarm passes, while a scattered
// swarm (e.g. mid-knockback or under heavy fire) fails.
export const CHOREO_FORMATION_RADIUS = 8;
// === U5: card mechanics polish === END

// ChoreoUltimate AoE radius — drones within this radius of the swarm centroid
// take damage on each tick the formation holds.
export const CHOREO_AOE_RADIUS = 15;

// Cage radius matches FormationSolver.cage (10u sphere) — slowing the enclosed
// lead drone by 60%.
export const CAGE_SURROUND_RADIUS = 10;

// RadarRing radius matches FormationSolver.radarRing (12u tilted ring) — used
// as the reveal zone (enemies inside this radius are marked for teammates).
export const RADAR_RING_RADIUS = 12;

// Critical hit system (CombatResolver). Roll happens AFTER all multiplicative
// damage buffs (Support aura, SyncDive, FocusFire, DefensiveWall reduction),
// so a crit boosts the effective post-buff damage. Tests that assert exact
// damage numbers should call `setCritChance(0)` (or rely on `seedRandom`
// which disables crits as a side-effect — see testHelpers).
export const CRIT_CHANCE = 0.10;        // 10% per attack
export const CRIT_MULTIPLIER = 1.5;     // damage × 1.5 on crit (then floored)
